import { BaseEffect } from '../BaseEffect.js';

/**
 * Protean clouds effect implementation
 * Based on "Protean clouds" by nimitz (https://www.shadertoy.com/view/3l23Rh)
 * Creates mesmerizing volumetric cloud formations with dynamic movement
 */
export class ProteanCloudsEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            speed: 1.0,           // Animation speed
            scale: 1.0,           // Overall scale of the effect
            density: 0.5,         // Cloud density
            distortion: 0.5,      // Amount of cloud distortion
            brightness: 1.0,      // Overall brightness
            saturation: 1.0,      // Color saturation
            colorTint: [0.5, 0.6, 1.0], // Base color tint (RGB)
            colorTintR: 0.5,      // Red component of color tint
            colorTintG: 0.6,      // Green component of color tint
            colorTintB: 1.0,      // Blue component of color tint
            blendAmount: 0.7,     // Amount to blend with source image
            animate: true         // Whether to animate the effect
        };

        super('protean-clouds', shaderManager, defaultParams);
    }

    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision highp float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_speed;
            uniform float u_scale;
            uniform float u_density;
            uniform float u_distortion;
            uniform float u_brightness;
            uniform float u_saturation;
            uniform vec3 u_colorTint;
            uniform float u_blendAmount;
            
            varying vec2 v_texcoord;

            mat2 rot(float a) {
                float c = cos(a), s = sin(a);
                return mat2(c, s, -s, c);
            }

            const mat3 m3 = mat3(0.33338, 0.56034, -0.71817, -0.87887, 0.32651, -0.15323, 0.15162, 0.69596, 0.61339) * 1.93;
            float mag2(vec2 p) { return dot(p, p); }
            float linstep(float mn, float mx, float x) { return clamp((x - mn)/(mx - mn), 0., 1.); }

            vec2 disp(float t) {
                return vec2(sin(t*0.22), cos(t*0.175)) * 2.0;
            }

            vec2 map(vec3 p, float time, float distAmt) {
                vec3 p2 = p;
                p2.xy -= disp(p.z).xy * distAmt;
                p.xy *= rot(sin(p.z + time) * 0.15 + time * 0.09);
                float cl = mag2(p2.xy);
                float d = 0.0;
                p *= 0.61;
                float z = 1.0;
                float trk = 1.0;
                float dspAmp = 0.3;
                for(int i = 0; i < 5; i++) {
                    p += sin(p.zxy * 0.75 * trk + time * trk * 0.8) * dspAmp;
                    d -= abs(dot(cos(p), sin(p.yzx)) * z);
                    z *= 0.57;
                    trk *= 1.4;
                    p = p * m3;
                }
                d = abs(d) * u_density - 2.5;
                return vec2(d + cl * 0.2 + 0.25, cl);
            }

            float getsat(vec3 c) {
                float mi = min(min(c.x, c.y), c.z);
                float ma = max(max(c.x, c.y), c.z);
                return (ma - mi)/(ma + 1e-7);
            }

            vec3 iLerp(vec3 a, vec3 b, float x) {
                vec3 ic = mix(a, b, x) + vec3(1e-6, 0., 0.);
                float sd = abs(getsat(ic) - mix(getsat(a), getsat(b), x));
                vec3 dir = normalize(vec3(2.0*ic.x - ic.y - ic.z, 2.0*ic.y - ic.x - ic.z, 2.0*ic.z - ic.y - ic.x));
                float lgt = dot(vec3(1.0), ic);
                float ff = dot(dir, normalize(ic));
                ic += 1.5 * dir * sd * ff * lgt;
                return clamp(ic, 0., 1.);
            }

            vec4 render(vec3 ro, vec3 rd, float time, float distAmt) {
                vec4 rez = vec4(0);
                const float ldst = 8.0;
                vec3 lpos = vec3(disp(time + ldst) * 0.5, time + ldst);
                float t = 1.5;
                float fogT = 0.0;
                
                for(int i = 0; i < 130; i++) {
                    if(rez.a > 0.99) break;

                    vec3 pos = ro + t * rd;
                    vec2 mpv = map(pos, time, distAmt);
                    float den = clamp(mpv.x - 0.3, 0., 1.) * 1.12;
                    float dn = clamp((mpv.x + 2.0), 0., 3.);
                    
                    vec4 col = vec4(0);
                    if(mpv.x > 0.6) {
                        vec3 lin = vec3(0.6, 0.5, 0.7);
                        col = vec4(sin(vec3(5.,0.4,0.2) + mpv.y*0.1 + sin(pos.z*0.4)*0.5 + 1.8)*0.5 + 0.5, 0.08);
                        col *= den*den*den;
                        col.rgb *= lin * linstep(4.,-2.5, mpv.x)*2.3;
                        float dif = clamp((den - map(pos+.8, time, distAmt).x)/9., 0.001, 1.);
                        dif += clamp((den - map(pos+.35, time, distAmt).x)/2.5, 0.001, 1.);
                        col.xyz *= den * (vec3(0.005,0.045,0.075) + 1.5*vec3(0.033,0.07,0.03)*dif);
                    }
                    
                    float fogC = exp(t*0.2 - 2.2);
                    col.rgba += vec4(0.06,0.11,0.11, 0.1) * clamp(fogC-fogT, 0., 1.);
                    fogT = fogC;
                    rez = rez + col*(1. - rez.a);
                    t += clamp(0.5 - dn*dn*.05, 0.09, 0.3);
                }
                return clamp(rez, 0.0, 1.0);
            }

            void main() {
                vec2 q = v_texcoord;
                vec2 p = (v_texcoord - 0.5) * vec2(u_resolution.x/u_resolution.y, 1.0);
                
                float time = u_time * u_speed * 0.5;
                vec3 ro = vec3(0.0, 0.0, time);
                
                ro += vec3(sin(time)*0.5, sin(time*1.)*0.0, 0.0);
                
                float dspAmp = 0.85 * u_distortion;
                ro.xy += disp(ro.z) * dspAmp;
                
                vec3 target = normalize(ro - vec3(disp(time + 3.5) * dspAmp, time + 3.5));
                vec3 rightdir = normalize(cross(target, vec3(0,1,0)));
                vec3 updir = normalize(cross(rightdir, target));
                rightdir = normalize(cross(updir, target));
                
                vec3 rd = normalize((p.x*rightdir + p.y*updir)*1.0 - target);
                rd.xy *= rot(-disp(time + 3.5).x * 0.2);
                
                vec4 scn = render(ro, rd, time, u_distortion);
                vec3 col = scn.rgb * u_colorTint;
                
                // Apply saturation
                float sat = getsat(col);
                col = mix(vec3(sat), col, u_saturation);
                
                // Apply brightness
                col *= u_brightness;
                
                // Apply vignette
                col *= pow(16.0*q.x*q.y*(1.0-q.x)*(1.0-q.y), 0.12)*0.7+0.3;

                // Blend with source image
                vec4 sourceColor = texture2D(u_image, v_texcoord);
                vec3 finalColor = mix(sourceColor.rgb, col, u_blendAmount);
                
                gl_FragColor = vec4(finalColor, 1.0);
            }
        `;

        await super.init(vertexSource, fragmentSource);
        
        // Ensure all uniforms are properly initialized
        this.setupUniforms();
        
        // Initialize with default parameters
        this.updateParams(this.params);
    }

    setupUniforms() {
        super.setupUniforms();
        
        // Add all required uniforms
        this.addUniform('u_image', { type: 'sampler2D' });
        this.addUniform('u_time', { type: '1f' });
        this.addUniform('u_resolution', { type: '2f' });
        this.addUniform('u_speed', { type: '1f' });
        this.addUniform('u_scale', { type: '1f' });
        this.addUniform('u_density', { type: '1f' });
        this.addUniform('u_distortion', { type: '1f' });
        this.addUniform('u_brightness', { type: '1f' });
        this.addUniform('u_saturation', { type: '1f' });
        this.addUniform('u_colorTint', { type: '3fv' });
        this.addUniform('u_blendAmount', { type: '1f' });
    }

    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        // Update all uniform values
        Object.assign(uniforms, {
            u_speed: { value: this.params.speed, type: '1f' },
            u_scale: { value: this.params.scale, type: '1f' },
            u_density: { value: this.params.density, type: '1f' },
            u_distortion: { value: this.params.distortion, type: '1f' },
            u_brightness: { value: this.params.brightness, type: '1f' },
            u_saturation: { value: this.params.saturation, type: '1f' },
            u_colorTint: { value: this.params.colorTint, type: '3fv' },
            u_blendAmount: { value: this.params.blendAmount, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    getUIConfig() {
        return {
            blendAmount: {
                min: 0,
                max: 1,
                step: 0.05,
                label: 'Effect Strength'
            },
            speed: {
                min: 0,
                max: 2,
                step: 0.1,
                label: 'Animation Speed'
            },
            scale: {
                min: 0.1,
                max: 2,
                step: 0.1,
                label: 'Scale'
            },
            density: {
                min: 0,
                max: 1,
                step: 0.05,
                label: 'Cloud Density'
            },
            distortion: {
                min: 0,
                max: 1,
                step: 0.05,
                label: 'Distortion'
            },
            brightness: {
                min: 0.5,
                max: 2,
                step: 0.1,
                label: 'Brightness'
            },
            saturation: {
                min: 0,
                max: 2,
                step: 0.1,
                label: 'Saturation'
            },
            colorTintR: {
                min: 0,
                max: 1,
                step: 0.1,
                label: 'Red Tint',
                onChange: (value) => {
                    this.params.colorTint[0] = value;
                }
            },
            colorTintG: {
                min: 0,
                max: 1,
                step: 0.1,
                label: 'Green Tint',
                onChange: (value) => {
                    this.params.colorTint[1] = value;
                }
            },
            colorTintB: {
                min: 0,
                max: 1,
                step: 0.1,
                label: 'Blue Tint',
                onChange: (value) => {
                    this.params.colorTint[2] = value;
                }
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 